import React, { useMemo, useState, useEffect } from "react";

export type PlaylistManagerProps = {
  state: any;                  // expects state.game.queue & state.game.queueIndex
  send: (msg: any) => void;    // WS sender from App
  defaultCollapsed?: boolean;
};

// Cache for video titles to avoid repeated API calls
const titleCache = new Map<string, string>();

function extractYouTubeIds(raw: string): string[] {
  const parts = raw.split(/[\s,\n]+/).map(s => s.trim()).filter(Boolean);
  const ids: string[] = [];
  const idRe = /([A-Za-z0-9_-]{11})/;
  
  for (const p of parts) {
    // Direct video ID
    if (/^[A-Za-z0-9_-]{11}$/.test(p)) { 
      ids.push(p); 
      continue; 
    }
    
    // Various YouTube URL formats
    let m = p.match(/[?&]v=([A-Za-z0-9_-]{11})/); 
    if (m) { ids.push(m[1]); continue; }
    
    m = p.match(/youtu\.be\/([A-Za-z0-9_-]{11})/); 
    if (m) { ids.push(m[1]); continue; }
    
    m = p.match(/shorts\/([A-Za-z0-9_-]{11})/); 
    if (m) { ids.push(m[1]); continue; }
    
    m = p.match(/embed\/([A-Za-z0-9_-]{11})/); 
    if (m) { ids.push(m[1]); continue; }
    
    // Fallback pattern match
    m = p.match(idRe); 
    if (m) { ids.push(m[1]); }
  }
  
  return Array.from(new Set(ids)); // Remove duplicates
}

// Fetch video title from YouTube's oEmbed API (no API key required)
async function fetchVideoTitle(videoId: string): Promise<string> {
  if (titleCache.has(videoId)) {
    return titleCache.get(videoId)!;
  }

  try {
    const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
    if (response.ok) {
      const data = await response.json();
      const title = data.title || `Video ${videoId.slice(0, 8)}...`;
      titleCache.set(videoId, title);
      return title;
    }
  } catch (error) {
    console.warn('Failed to fetch title for', videoId, error);
  }
  
  // Fallback to formatted ID
  const fallback = `Video ${videoId.slice(0, 8)}...`;
  titleCache.set(videoId, fallback);
  return fallback;
}

function VideoItem({ 
  id, 
  index, 
  isPlaying, 
  onJump, 
  onRemove 
}: { 
  id: string; 
  index: number; 
  isPlaying: boolean; 
  onJump: () => void; 
  onRemove: () => void; 
}) {
  const [title, setTitle] = useState<string>(`Video ${id.slice(0, 8)}...`);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    
    fetchVideoTitle(id).then(fetchedTitle => {
      if (mounted) {
        setTitle(fetchedTitle);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
    };
  }, [id]);

  return (
    <div className={`group flex items-center gap-3 p-3 rounded-xl border transition-all hover:bg-slate-800/60 ${
      isPlaying 
        ? "border-red-500/60 bg-red-500/10 shadow-lg shadow-red-500/10" 
        : "border-slate-700/50 bg-slate-800/40"
    }`}>
      {/* Position indicator */}
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${
        isPlaying 
          ? "bg-red-500 text-white" 
          : "bg-slate-700 text-slate-300"
      }`}>
        {index + 1}
      </div>
      
      {/* Video info */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-white truncate">
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-slate-400">Loading title...</span>
            </div>
          ) : (
            title
          )}
        </div>
        <div className="text-xs text-slate-400 font-mono mt-1">
          {id}
        </div>
      </div>
      
      {/* Status indicators */}
      {isPlaying && (
        <div className="bg-green-500/20 text-green-400 px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          Playing
        </div>
      )}
      
      {/* Actions */}
      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button 
          onClick={onJump} 
          className="bg-blue-600/80 hover:bg-blue-500/80 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors btn-bounce"
          title="Play this video"
        >
          Play
        </button>
        <button 
          onClick={onRemove} 
          className="bg-red-600/80 hover:bg-red-500/80 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors btn-bounce"
          title="Remove from queue"
        >
          Remove
        </button>
      </div>
    </div>
  );
}

export default function PlaylistManager({ state, send, defaultCollapsed = false }: PlaylistManagerProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [input, setInput] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const queue: string[] = state?.game?.queue ?? [];
  const index: number = state?.game?.queueIndex ?? -1;

  const hasItems = queue.length > 0;
  const atStart = index <= 0;
  const atEnd = index < 0 || index >= queue.length - 1;
  const currentIndex = Math.max(0, index);

  // Current and next video info with titles
  const [currentTitle, setCurrentTitle] = useState<string>("No video selected");
  const [nextTitle, setNextTitle] = useState<string>("End of queue");

  // Fetch titles for current and next videos
  useEffect(() => {
    if (index >= 0 && index < queue.length) {
      fetchVideoTitle(queue[index]).then(setCurrentTitle);
    } else {
      setCurrentTitle("No video selected");
    }

    if (index + 1 < queue.length) {
      fetchVideoTitle(queue[index + 1]).then(setNextTitle);
    } else {
      setNextTitle("End of queue");
    }
  }, [queue, index]);

  // Actions
  const add = () => {
    const ids = extractYouTubeIds(input);
    if (ids.length) { 
      send({ type: "QUEUE_ADD", ids }); 
      setInput(""); 
    }
  };

  const clear = () => {
    if (window.confirm('Clear entire playlist?')) {
      send({ type: "QUEUE_CLEAR" });
    }
  };

  const prev = () => send({ type: "QUEUE_PREV" });
  const next = () => send({ type: "QUEUE_NEXT" });
  const jump = (i: number) => send({ type: "QUEUE_JUMP", index: i });
  const removeAt = (i: number) => send({ type: "QUEUE_REMOVE", index: i });

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      add();
    }
  };

  const queueStats = useMemo(() => {
    const total = queue.length;
    const remaining = Math.max(0, total - (index + 1));
    const duration = total * 3.5; // Rough estimate: 3.5 min average per video
    
    return { total, remaining, duration };
  }, [queue, index]);

  return (
    <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-800/50 rounded-2xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center shadow-xl">
              <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">Playlist</h3>
              <p className="text-slate-400">
                {queueStats.total} videos • ~{Math.round(queueStats.duration)}min total
              </p>
            </div>
          </div>
          
          <button 
            onClick={() => setCollapsed(c => !c)} 
            className="bg-slate-800/70 hover:bg-slate-700/70 border border-slate-700/50 rounded-xl px-4 py-2 text-sm font-medium text-white transition-all btn-bounce"
          >
            {collapsed ? "Expand" : "Collapse"}
          </button>
        </div>

        {/* Current Status with Titles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/30">
            <div className="text-sm text-slate-400 mb-2">Now Playing</div>
            <div className="font-medium text-white mb-1 line-clamp-2">{currentTitle}</div>
            <div className="text-xs text-slate-500">
              {index >= 0 ? (
                <>
                  <span>{index + 1} of {queue.length}</span>
                  {queue[index] && (
                    <span className="ml-2 font-mono opacity-60">{queue[index].slice(0, 8)}...</span>
                  )}
                </>
              ) : (
                'Queue empty'
              )}
            </div>
          </div>
          
          <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/30">
            <div className="text-sm text-slate-400 mb-2">Up Next</div>
            <div className="font-medium text-white mb-1 line-clamp-2">{nextTitle}</div>
            <div className="text-xs text-slate-500">
              {queueStats.remaining > 0 ? (
                <>
                  <span>{queueStats.remaining} videos remaining</span>
                  {queue[index + 1] && (
                    <span className="ml-2 font-mono opacity-60">{queue[index + 1].slice(0, 8)}...</span>
                  )}
                </>
              ) : (
                'No more videos'
              )}
            </div>
          </div>
        </div>
      </div>

      {!collapsed && (
        <div className="px-6 pb-6 space-y-6">
          {/* Add Videos Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-white">Add Videos</h4>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-xs text-slate-400 hover:text-slate-300 transition-colors"
              >
                {showAdvanced ? 'Simple' : 'Advanced'}
              </button>
            </div>
            
            <div className="space-y-3">
              {showAdvanced ? (
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Add multiple videos (one per line):"
                  className="w-full bg-slate-800/70 border border-slate-700/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                  rows={4}
                />
              ) : (
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Paste YouTube URL or video ID..."
                  className="w-full bg-slate-800/70 border border-slate-700/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                />
              )}
              
              <div className="flex gap-2">
                <button 
                  onClick={add} 
                  disabled={!input.trim()} 
                  className="flex-1 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 disabled:from-slate-600 disabled:to-slate-700 text-white px-4 py-2 rounded-xl font-medium transition-all shadow-lg disabled:cursor-not-allowed btn-bounce"
                >
                  Add to Queue
                </button>
                {input.trim() && (
                  <button
                    onClick={() => setInput('')}
                    className="bg-slate-700/70 hover:bg-slate-600/70 text-white px-4 py-2 rounded-xl transition-all btn-bounce"
                    title="Clear input"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Playback Controls */}
          <div className="space-y-4">
            <h4 className="font-semibold text-white">Playback Controls</h4>
            
            <div className="flex flex-wrap gap-3">
              <button 
                onClick={prev} 
                disabled={!hasItems || atStart} 
                className="flex items-center gap-2 bg-slate-800/70 hover:bg-slate-700/70 border border-slate-700/50 rounded-xl px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all btn-bounce"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.445 14.832A1 1 0 0010 14v-2.798l5.445 3.63A1 1 0 0017 14V6a1 1 0 00-1.555-.832L10 8.798V6a1 1 0 00-1.555-.832l-6 4a1 1 0 000 1.664l6 4z"/>
                </svg>
                Previous
              </button>
              
              <button 
                onClick={next} 
                disabled={!hasItems || atEnd} 
                className="flex items-center gap-2 bg-slate-800/70 hover:bg-slate-700/70 border border-slate-700/50 rounded-xl px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all btn-bounce"
              >
                Next
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798l-5.445-3.63z"/>
                </svg>
              </button>
              
              <button 
                onClick={clear} 
                disabled={!hasItems} 
                className="flex items-center gap-2 bg-red-600/80 hover:bg-red-500/80 border border-red-500/50 rounded-xl px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all btn-bounce"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
                Clear All
              </button>
            </div>
          </div>

          {/* Queue List with Titles */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-white">Queue ({queue.length})</h4>
              {queue.length > 5 && (
                <div className="text-xs text-slate-400">
                  Showing all {queue.length} videos
                </div>
              )}
            </div>
            
            {queue.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <div className="w-16 h-16 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
                <p className="text-lg font-medium">Queue is empty</p>
                <p className="text-sm mt-1">Add some videos to get started!</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                {queue.map((id, i) => (
                  <VideoItem
                    key={`${id}-${i}`}
                    id={id}
                    index={i}
                    isPlaying={i === index}
                    onJump={() => jump(i)}
                    onRemove={() => removeAt(i)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}